import React from 'react';
import './AddPage.scss';
import { Button, Card, Checkbox, Dropdown, Icon, Input, Textarea, Typography } from '@design-system/index';
import { Formik } from 'formik';
import { ApartmentToCreate } from '@services/member/apartment/apartment.types';
import { AppartementsLine, Plus, Delete } from '@icons';
import { useAddPageVM } from './useAddPageVM';
import { FileUpload } from '@components/FileUpload/FileUpload';
import { CdnFile } from '@services/core/cdn/file/file';
import { AutocompleteInput } from '@components/AutocompleteInput/AutocompleteInput';
import { APARTMENT_TYPE } from '@utils/enumTranslation';
import { PriceType } from '@services/member/list/price-types/price-types.types';

export default function AddPage() {
    const { submitForm, isLoading, apartment, isEdit, priceTypes, selectedPriceType, setSelectedPriceType } = useAddPageVM();
    return (
        <div className="add-apartment-container">
            <Card
                isLoading={isLoading}
                headerType="full"
                showBack
                headerTitle={isEdit ? 'Modification logement' : 'Création nouveau logement'}
                headerIcon={<Icon icon={<AppartementsLine />} size={20} color="primary" />}
            >
                <>
                    <Formik
                        initialValues={
                            apartment || {
                                reference: '',
                                label: '',
                                surface: '',
                                numberOfRooms: '',
                                numberOfBeds: '',
                                description: '',
                                type: '',
                                floor: '',
                                price: {
                                    deposit: '',
                                    cleaningFee: '',
                                    cancellationFee: ''
                                },
                                apartmentPrices: [],
                                address: {
                                    address: '',
                                    city: '',
                                    zipCode: '',
                                    additionalAddress: ''
                                },
                                photosIds: [],
                                photos: [],
                                documentsIds: [],
                                documents: [],
                                template: null,
                                additionalDescription: '',
                                hasTouristTax: false
                            }
                        }
                        enableReinitialize
                        onSubmit={async (values: ApartmentToCreate & { template?: PriceType | null }) => {
                            await submitForm(values);
                        }}
                    >
                        {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => {
                            return (
                                <form onSubmit={handleSubmit} className="form">
                                    <div className="form-section-container">
                                        <div className="form-section" id="main-section">
                                            <Typography intrinsic="h3">Informations principales</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="Référence"
                                                            name="reference"
                                                            value={values.reference}
                                                            required
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            label="Intitulé du logement"
                                                            name="label"
                                                            value={values.label}
                                                            required
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Dropdown
                                                            label="Type de logement"
                                                            required
                                                            options={Object.entries(APARTMENT_TYPE).map(([key, value]) => ({
                                                                id: key,
                                                                label: value
                                                            }))}
                                                            value={values.type}
                                                            onOptionChange={(option) => setFieldValue('type', option.id)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section" id="informations-section">
                                            <Typography intrinsic="h3">Informations du logement</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <AutocompleteInput
                                                            onPlaceSelect={(place) => {
                                                                setFieldValue('address.address', place.address);
                                                                setFieldValue('address.city', place.city);
                                                                setFieldValue('address.zipCode', place.postal_code);
                                                            }}
                                                        >
                                                            <Input
                                                                label="Adresse"
                                                                required
                                                                name="address.address"
                                                                value={values.address.address}
                                                                onChange={handleChange}
                                                            />
                                                        </AutocompleteInput>
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="Complément d'adresse"
                                                            name="address.additionalAddress"
                                                            value={values.address.additionalAddress}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="Ville"
                                                            required
                                                            name="address.city"
                                                            value={values.address.city}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            label="Code postal"
                                                            required
                                                            name="address.zipCode"
                                                            value={values.address.zipCode}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            label="Étage"
                                                            required
                                                            name="floor"
                                                            value={values.floor}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            type="number"
                                                            required
                                                            name="surface"
                                                            rightContent="m²"
                                                            label="Surface"
                                                            value={values.surface}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            type="number"
                                                            required
                                                            name="numberOfRooms"
                                                            label="Nombre de pièces"
                                                            value={values.numberOfRooms}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            type="number"
                                                            required
                                                            name="numberOfBeds"
                                                            label="Nombre de couchages"
                                                            value={values.numberOfBeds}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            type="text"
                                                            name="additionalDescription"
                                                            label="Description complémentaire (présente sur la convention)"
                                                            value={values.additionalDescription}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section" id="tarifs-section">
                                            <Typography intrinsic="h3">Tarifs</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line" style={{ gap: '14px' }}>
                                                    <Dropdown
                                                        name="template"
                                                        label="Tarif à ajouter"
                                                        value={selectedPriceType}
                                                        onOptionChange={(option) => {
                                                            const selected = priceTypes.find(
                                                                (priceType) => priceType.label === option.label
                                                            );
                                                            setSelectedPriceType(selected || null);
                                                        }}
                                                        options={priceTypes.map((priceType) => ({
                                                            label: priceType.label,
                                                            value: priceType.code,
                                                            id: priceType.id
                                                        }))}
                                                    />
                                                    <Button
                                                        size="small"
                                                        color="grey"
                                                        icon={<Icon icon={<Plus />} size={12} />}
                                                        type="button"
                                                        bordered
                                                        onClick={() => {
                                                            if (!selectedPriceType) return;
                                                            if (
                                                                values.apartmentPrices.find(
                                                                    (price) => price.priceType.id === selectedPriceType.id
                                                                )
                                                            )
                                                                return;
                                                            setFieldValue('apartmentPrices', [
                                                                ...values.apartmentPrices,
                                                                {
                                                                    priceType: selectedPriceType,
                                                                    amount: 0
                                                                }
                                                            ]);
                                                        }}
                                                    />
                                                </div>
                                                {Array.from({ length: Math.ceil(values.apartmentPrices.length / 3) }, (_, lineIndex) => (
                                                    <div className="form-line" key={lineIndex}>
                                                        {values.apartmentPrices
                                                            .slice(lineIndex * 3, (lineIndex + 1) * 3)
                                                            .map((price, index) => {
                                                                const actualIndex = lineIndex * 3 + index;
                                                                return (
                                                                    <React.Fragment key={actualIndex}>
                                                                        <div className="form-group">
                                                                            <div className="form-group-input-container">
                                                                            <Input
                                                                                type="number"
                                                                                required
                                                                                name={`apartmentPrices.${actualIndex}.amount`}
                                                                                rightContent="€"
                                                                                label={
                                                                                    priceTypes.find(
                                                                                        (priceType: PriceType) =>
                                                                                            priceType.id === price.priceType.id
                                                                                    )?.label || ''
                                                                                }
                                                                                value={values.apartmentPrices[actualIndex].amount}
                                                                                    onChange={handleChange}
                                                                                />
                                                                                <Icon icon={<Delete />} size={20} onClick={() => {
                                                                                    const newPrices = values.apartmentPrices.filter((_, i) => i !== actualIndex);
                                                                                    setFieldValue('apartmentPrices', newPrices);
                                                                                }} />
                                                                            </div>
                                                                        </div>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                    </div>
                                                ))}
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            type="number"
                                                            required
                                                            name="price.deposit"
                                                            rightContent="€"
                                                            label="Caution dégâts"
                                                            value={values.price.deposit}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            type="number"
                                                            required
                                                            name="price.cleaningFee"
                                                            rightContent="€"
                                                            label="Caution ménage"
                                                            value={values.price.cleaningFee}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            type="number"
                                                            required
                                                            name="price.cancellationFee"
                                                            rightContent="€"
                                                            label="Caution annulation"
                                                            value={values.price.cancellationFee}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                    <Checkbox
                                                        checked={!!values.hasTouristTax}
                                                        onChange={(v) => {
                                                            setFieldValue('hasTouristTax', v);
                                                        }}
                                                    >
                                                        <Typography intrinsic="p" variant="p2" color="grey1">
                                                            Taxe de séjour
                                                        </Typography>
                                                    </Checkbox>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section" id="description-section">
                                            <Typography intrinsic="h3">Description</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Textarea
                                                            label="Description"
                                                            wysiwyg
                                                            required
                                                            name="description"
                                                            value={values.description ?? ''}
                                                            onChange={(value) => {
                                                                setFieldValue('description', value);
                                                            }}
                                                            height={220}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section" id="photos-section">
                                            <Typography intrinsic="h3">Photos</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <div className="form-group-file-upload-grid">
                                                            <FileUpload
                                                                width="338px"
                                                                height="338px"
                                                                variant="light"
                                                                isMultiple={false}
                                                                isImage={true}
                                                                files={values.photos?.[0]}
                                                                position={0}
                                                                onSuccess={(files: CdnFile[]) => {
                                                                    const newImages = values.photos ? [...values.photos] : [];
                                                                    newImages[0] = files[0];
                                                                    setFieldValue('photos', newImages);
                                                                }}
                                                            />
                                                            <FileUpload
                                                                width="338px"
                                                                height="338px"
                                                                variant="light"
                                                                isMultiple={false}
                                                                isImage={true}
                                                                files={values.photos?.[1]}
                                                                position={1}
                                                                onSuccess={(files: CdnFile[]) => {
                                                                    const newImages = values.photos ? [...values.photos] : [];
                                                                    newImages[1] = files[0];
                                                                    setFieldValue('photos', newImages);
                                                                }}
                                                            />
                                                            <div className="form-group-file-upload-grid-items">
                                                                {[
                                                                    ...Array(
                                                                        Math.max(
                                                                            4,
                                                                            (values.photos?.filter((img) => img?.id != null).length || 0) -
                                                                                1
                                                                        )
                                                                    )
                                                                ].map((_, index) => (
                                                                    <FileUpload
                                                                        key={index}
                                                                        width="161px"
                                                                        height="161px"
                                                                        variant="light"
                                                                        isMultiple={false}
                                                                        isImage={true}
                                                                        position={index + 2}
                                                                        files={values.photos?.[index + 2]}
                                                                        onSuccess={(files: CdnFile[]) => {
                                                                            const newImages = values.photos ? [...values.photos] : [];
                                                                            newImages[index + 2] = files[0];
                                                                            setFieldValue('photos', newImages);
                                                                        }}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        
                                        <div className="form-section" id="documents-section">
                                            <Typography intrinsic="h3">Documents</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <FileUpload
                                                            width="100%"
                                                            height="214px"
                                                            isMultiple={true}
                                                            isImage={false}
                                                            accept="application/pdf"
                                                            files={values.documents}
                                                            onSuccess={(files: CdnFile[]) => {
                                                                setFieldValue(
                                                                    'documentsIds',
                                                                    files.map((file) => file.id)
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    <div className="action-buttons">
                                        <Button type="submit" disabled={isSubmitting} variant="primary">
                                            {isEdit ? 'Valider la modification' : 'Valider la création'}
                                        </Button>
                                    </div>
                                </form>
                            );
                        }}
                    </Formik>
                </>
            </Card>
        </div>
    );
}
